import {
  clearProgress,
  showSweepstakesBadgeModal,
  updateActivitiesAction,
  updateAffiliate,
  updateAdobeSettings,
  updateConfig,
  updateCurrentLanguage,
  updateFinishedTopics,
  updateKeyTermsAction,
  updateLanguages,
  updateModulesAction,
  updatePagesAction,
  updatePartsAction,
  updateProgress,
  updateSharesCount,
  updateSharesId,
  updateTopicsAction,
  updateUserAction,
  updateFinishedSections,
} from "./actions";
import _ from "lodash";
import { normalize } from "normalizr";
import {
  activitySchema,
  keyTermSchema,
  moduleSchema,
  pageSchema,
  topicsSchema,
} from "./schemas";
import {
  clearData,
  getLanguage,
  getUserData,
  hasData,
  persistLanguage,
  persistUser,
} from "./persistStore";
import makeRequest from "../utils/makeRequest";
import { logEvent, logInteractionEvent } from "../utils/appInsight";
import constants from "../utils/constants";
import i18n from "../i18n";
import { initialCurrentPosition } from "./reducers/progress";
import { getDeeplink } from "../store/persistStore";

export const initialLoadThunk = () => async (dispatch) => {
  await dispatch(fetchAdobeSettingsThunk());
  const language = await dispatch(fetchLanguageThunk());

  if (hasData()) {
    const user = getUserData();
    const [progress] = await Promise.all([
      dispatch(fetchUserProgressThunk()),
      // TODO hidden until benefits ready to deploy
      // dispatch(ensureBenefits()),
      dispatch(fetchAffiliateInfo()),
      dispatch(fetchSharesCountThunk()),
      dispatch(updateUserAction(user)),
      dispatch(fetchContentItemsThunk(language)),
    ]);
    return progress;
  }

  return true;
};

export const ensureBenefits = () => async () => {
  await makeRequest("get", "/api/benefits");
};

export const fetchAdobeSettingsThunk = () => async (dispatch) => {
  try {
    const payload = await makeRequest("get", "/api/adobe");
    dispatch(updateAdobeSettings(payload));
  } catch (e) {
    console.error("Error fetching Adobe settings");
    dispatch(updateAdobeSettings({}));
  }
};

export const fetchAffiliateInfo = () => async (dispatch) => {
  const payload = await makeRequest("get", "/api/affiliates");
  dispatch(updateAffiliate(payload));
};

export const fetchSharesCountThunk = () => async (dispatch) => {
  const [shares, id] = await Promise.all([
    makeRequest("get", "/api/share"),
    makeRequest("get", "/api/share/get-id"),
  ]);
  dispatch(updateSharesCount(shares));
  dispatch(updateSharesId(id));
  return shares;
};

export const incrementSharesCountThunk = () => async (dispatch) => {
  const shares = await makeRequest("post", "/api/share");
  dispatch(updateSharesCount(shares));
};

export const fetchLanguageThunk = () => async (dispatch) => {
  const lang = await makeRequest("get", "/api/language");
  const language = lang?.lang === "" ? getLanguage() : lang?.lang;

  dispatch(updateCurrentLanguage(language));
  const languageFile = await makeRequest("post", "/api/language/lang-file", {
    lang: language,
  });
  if (languageFile && languageFile.data) {
    const langData = JSON.parse(languageFile.data);
    dispatch(updateLanguages(langData));
    _.forEach(langData, (val, key) => {
      i18n.addResourceBundle(key, "translation", val.translation);
    });
  }

  i18n.changeLanguage(language);
  persistLanguage(language);
  return language;
};

export const setLanguageThunk = (lang, isAuthenticated) => async (dispatch) => {
  if (isAuthenticated) {
    await makeRequest("post", "/api/language", { lang });
    await makeRequest("post", `/api/servicebus/lang/${lang}`);
  }
  const languageFile = await makeRequest("post", "/api/language/lang-file", {
    lang,
  });
  if (languageFile && languageFile.data) {
    const langData = JSON.parse(languageFile.data);
    dispatch(updateLanguages(langData));
    _.forEach(langData, (val, key) => {
      i18n.addResourceBundle(key, "translation", val.translation);
    });
  }
  i18n.changeLanguage(lang);
  persistLanguage(lang);
  dispatch(updateCurrentLanguage(lang));
  return true;
};

export const fetchContentItemsThunk = (lang = "en") => async (dispatch) => {
  const items = await makeRequest("get", `/api/contentitems?lang=${lang}`);
  const modules = _.filter(
    items,
    (i) => i.category && i.category.includes(constants.contentTypes.module)
  ).map((i) => ({
    ...i,
    data: JSON.parse(i.data),
  }));
  dispatch(updateModulesAction(normalize(modules, [moduleSchema])));
  const pages = _.filter(
    items,
    (i) => i.category && i.category.includes(constants.contentTypes.page)
  ).map((i) => ({
    ...i,
    data: JSON.parse(i.data),
  }));
  dispatch(updatePagesAction(normalize(pages, [pageSchema])));
  const topics = _.filter(
    items,
    (i) => i.category && i.category.includes(constants.contentTypes.topic)
  ).map((i) => ({
    ...i,
    data: JSON.parse(i.data),
  }));
  dispatch(updateTopicsAction(normalize(topics, [topicsSchema])));
  const keyTerms = _.filter(
    items,
    (i) => i.category && i.category.includes(constants.contentTypes.keyTerm)
  ).map((i) => ({
    ...i,
    data: JSON.parse(i.data),
  }));
  dispatch(updateKeyTermsAction(normalize(keyTerms, [keyTermSchema])));
  const activities = _.filter(
    items,
    (i) => i.category && i.category.includes(constants.contentTypes.activity)
  ).map((i) => ({
    ...i,
    data: i.data ? JSON.parse(i.data) : null,
  }));
  dispatch(updateActivitiesAction(normalize(activities, [activitySchema])));

  const parts = _.filter(
    items,
    (i) => i.category && i.category.includes(constants.contentTypes.part)
  );
  let partsHash = {};
  topics.forEach((topic) => {
    partsHash[topic.contentKey] = _.orderBy(
      _.filter(parts, { parentContentId: topic.contentKey }),
      "contentKey",
      "asc"
    ).map((item) => ({
      ...item,
      data: item.data ? JSON.parse(item.data) : null,
    }));
  });
  dispatch(updatePartsAction({ entities: { parts: partsHash } }));

  const languageFile = _.find(
    items,
    (i) => i.category && i.category.includes(constants.contentTypes.languages)
  );
  if (languageFile && languageFile.data) {
    const langData = JSON.parse(languageFile.data);
    dispatch(updateLanguages(langData));
    _.forEach(langData, (val, key) => {
      i18n.addResourceBundle(key, "translation", val.translation);
    });
  }

  const configuration = _.find(
    items,
    (i) =>
      i.category && i.category.includes(constants.contentTypes.configuration)
  );
  if (configuration && configuration.data) {
    const configData = JSON.parse(configuration.data);
    dispatch(updateConfig(configData));
  }

  return items;
};

const progressSectionUpdate = (progress) => {
  if (getDeeplink()) {
    if (getDeeplink().indexOf("/agile") != -1 || progress.section === 'PMAgile') {
      progress.section = 'PMAgile';
    } else {
      progress.section = 'PMWaterfall';
    }
  }
}

export const fetchUserProgressThunk = () => async (dispatch) => {
  const progress = await makeRequest("get", "/api/progress");
  progressSectionUpdate(progress);

  try {
    if (progress.currentPosition === "/app") {
      progress.currentPosition = initialCurrentPosition;
    } else {
      const result = JSON.parse(progress.currentPosition);
      progress.currentPosition = {};

      _.forEach(result, (val, key) => {
        progress.currentPosition[key] = {
          section: val.section,
          agilePath: val.agilePath.includes("undefined")
            ? "/app"
            : val.agilePath,
          waterfallPath: val.waterfallPath.includes("undefined")
            ? "/app"
            : val.waterfallPath,
        };
      });
    }
  } catch (err) {
    console.log("Error with progress parsing", err);
  }

  dispatch(updateProgress(progress));
  return progress;
};

export const userLoggedInThunk = (user) => async (dispatch) => {
  await persistUser(user);
  // await dispatch(updateUserAction(user));
  logEvent({
    name: "UserLoggedIn",
    properties: {
      user: {
        email: user.email,
        id: user.id,
      },
    },
  });
  logInteractionEvent("UserLoggedIn", {});
  // return await dispatch(fetchUserProgressThunk());
  return await dispatch(initialLoadThunk());
};

export const userLoggedOutThunk = (user, loggedOutUser) => async (dispatch) => {
  await dispatch(updateUserAction(user));
  await dispatch(clearProgress());
  clearData();
  logEvent({
    name: "UserLoggedOut",
    properties: {
      user: loggedOutUser,
    },
  });
  return true;
};

export const userAcceptedTermsThunk = () => async (dispatch, getState) => {
  const { user } = getState();
  if (localStorage.getItem("org")) {
    const affiliateData = {
      org: localStorage.getItem("org"),
      sync: localStorage.getItem("sync"),
    };
    logEvent({
      name: "Affiliate",
      properties: affiliateData,
    });
    makeRequest("post", "/api/affiliates", affiliateData);
    makeRequest("post", `/api/servicebus/affiliate`, affiliateData);
    dispatch(updateAffiliate(affiliateData));

    localStorage.removeItem("org");
    localStorage.removeItem("sync");
  }
  await makeRequest("post", "/api/progress/terms", {
    progress: "true",
  });
  logEvent({
    name: "TermsAccepted",
  });
  logInteractionEvent("TermsAccepted", {});
  // TODO hidden until sweepstakes ready to deploy
  // window.Gleam = window.Gleam || [];
  // console.log("TERMS ACCEPTED", user, window.Gleam);
  // window.Gleam.push(["TermsAccepted", user.email]);
  await dispatch(updateProgress({ termsAccepted: true }));
  return true;
};

export const userFinishedAssessmentThunk = (answers) => async (dispatch) => {
  await makeRequest("post", "/api/progress/assessment", {
    progress: JSON.stringify(answers),
  });
  logEvent({
    name: "AssessmentCompleted",
    properties: {
      answers,
    },
  });
  logInteractionEvent("AssessmentCompleted", {
    answers,
  });
  await dispatch(updateProgress({ assessmentDone: true }));

  //prepare answers for service bus
  let serviceBusAnswers = {};
  const arrayOfAnswers = _.values(answers);

  for (let i = 0; i < 10; i++) {
    const number = i < 9 ? `0${i + 1}` : `${i + 1}`;
    const name = `Attr${number}Name`;
    const value = `Attr${number}Value`;
    serviceBusAnswers[name] = arrayOfAnswers[i]
      ? arrayOfAnswers[i].questionTitle
      : null;
    serviceBusAnswers[value] = arrayOfAnswers[i]
      ? arrayOfAnswers[i].answer.title
      : null;
  }
  dispatch(serviceBusAssessmentCompleted(serviceBusAnswers));
};

export const userFinishedOrientationThunk = () => async (dispatch) => {
  await makeRequest("post", "/api/progress/orientation", {
    progress: "true",
  });
  logEvent({
    name: "OrientationCompleted",
  });
  logInteractionEvent("OrientationCompleted", {});
  await dispatch(updateProgress({ orientationDone: true }));
};

export const checkSweepstakes = (finishedTopics) => async (
  dispatch,
  getState
) => {
  const { config, progress } = getState();
  const doneSweepstakes = JSON.parse(progress.sweepstakes);
  const sweepstake = config.sweepstakes.find((x) => {
    return (
      finishedTopics.length >= parseInt(x.triggerAfter) &&
      !doneSweepstakes.includes(x.id)
    );
  });

  if (sweepstake) {
    dispatch(showSweepstakesBadgeModal(sweepstake.id));
    doneSweepstakes.push(sweepstake.id);
    dispatch(
      updateProgress({
        ...progress,
        sweepstakes: JSON.stringify(doneSweepstakes),
      })
    );
    await makeRequest("post", "/api/progress/sweepstakes", {
      progress: JSON.stringify(doneSweepstakes),
    });
  }
};

export const userFinishedTopicThunk = (finishedTopics) => async (dispatch) => {
  const safeArray = _.uniq(finishedTopics);
  await makeRequest("put", "/api/progress", {
    progress: JSON.stringify(safeArray),
  });
  await dispatch(updateFinishedTopics(safeArray));
  // TODO hidden until sweepstakes ready to deploy
  // dispatch(checkSweepstakes(safeArray));
  return safeArray;
};

export const userFinishedSubTopicThunk = (finishedSubTopics) => async (dispatch) => {
  const safeArray = _.uniq(finishedSubTopics);
  await dispatch(updateFinishedSections(safeArray));
  await makeRequest("put", "/api/progress/subtopics", {
    progress: JSON.stringify(safeArray),
  });
  return safeArray;
};

export const userSetsSectionThunk = (section) => async (dispatch) => {
  const res = await makeRequest("post", "/api/progress/section", {
    progress: section,
  });
  await dispatch(serviceBusUpdateCourse(section));
  await dispatch(updateProgress({ section }));
  return res;
};

export const userCompletesSectionThunk = (section) => async (
  dispatch,
  getState
) => {
  // TODO hidden until sweepstakes ready to deploy
  // const { user } = getState();
  // window.Gleam = window.Gleam || [];
  // window.Gleam.push(["SectionCompleted", user.email]);
  const res = await makeRequest("post", "/api/progress/course-completion", {
    progress: section,
  });
  logEvent({
    name: "SectionCompleted",
    properties: {
      section,
    },
  });
  logInteractionEvent("SectionCompleted", {
    section,
  });
  await dispatch(serviceBusUpdateCourse(section));
  return res;
};

export const updateCurrentPositionThunk = (path) => async (
  dispatch,
  getState
) => {
  const ui = getState().ui;
  const progress = getState().progress;
  const currentPosition = progress.currentPosition;
  const reqPath = progress.section === "PMAgile" ? "agilePath" : "waterfallPath";
  currentPosition[ui.lang] = {
    ...currentPosition[ui.lang],
    section: progress.section
  };
  if (path.includes('agile') && progress.section==="PMAgile") {
    currentPosition[ui.lang][reqPath] = path;
  }
  else if (!path.includes('agile') && progress.section==="PMWaterfall") {
    currentPosition[ui.lang][reqPath] = path;
  }
  const res = await makeRequest("post", "/api/progress/position", {
    progress: JSON.stringify(currentPosition),
  });
  await dispatch(updateProgress({ currentPosition }));
  return res;
};

export const serviceBusLoggedIn = () => async () => {
  return await makeRequest("post", "/api/servicebus/loggedin");
};

export const serviceBusAssessmentCompleted = (answers) => async () => {
  return await makeRequest("post", "/api/servicebus/assessment", answers);
};

export const serviceBusUpdateCourse = (section) => async () => {
  return await makeRequest(
    "post",
    `/api/servicebus/course-progress/${section}`
  );
};
